import type { Property } from '../../types';
import type { MapProperty } from './types';

/** Price per square foot from price / building size, when both are present. */
export function pricePerSqft(p: MapProperty): number | undefined {
  return p.price && p.building_size ? Math.round(p.price / p.building_size) : undefined;
}

/**
 * Adapt a map property to the richer Property shape used by PropertyComparison.
 * Financial fields the map doesn't carry (cap_rate, noi) are left undefined and
 * render as "—"; price_per_sqft is derived here.
 */
export function toComparisonProperty(p: MapProperty): Property {
  return {
    id: p.id,
    created_by: '',
    created_at: '',
    updated_at: '',
    address: p.address,
    city: p.city,
    state: p.state,
    zip_code: p.zip,
    property_type: p.property_type as Property['property_type'],
    building_size: p.building_size,
    year_built: p.year_built,
    price: p.price,
    price_per_sqft: pricePerSqft(p),
    primary_photo_url: p.primary_photo_url,
  };
}

/** Download the given properties as a CSV comp sheet. */
export function exportCompsCsv(rows: MapProperty[], filename = 'apex-comps.csv'): void {
  const headers = ['Address', 'City', 'State', 'Zip', 'Type', 'Building SF', 'Price', '$/SF', 'Year Built'];
  const esc = (v: unknown) => (v == null ? '' : `"${String(v).replace(/"/g, '""')}"`);
  const lines = [headers.join(',')];
  for (const p of rows) {
    lines.push([
      p.address, p.city, p.state, p.zip, p.property_type,
      p.building_size, p.price, pricePerSqft(p), p.year_built,
    ].map(esc).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
